import moment from "moment-timezone";
import {
  accessControlStatus,
  getBookingAccessControlState,
} from "../../equipment-list/accessControlStatus";

const CLICK_TO_CREATE_DISABLED = "disabled";
const CLICK_TO_CREATE_SINGLE = "single";

export const getCurrentHalfHourSlotUtc = (siteTimezone) => {
  const now = moment.tz(siteTimezone);

  return now
    .clone()
    .seconds(0)
    .milliseconds(0)
    .minutes(Math.floor(now.minutes()  / 30) * 30 - 1)
    .utc()
    .format();
};

const getStartedBookingRestriction = ({ newResource, newStart, nowUtc, oldEnd, oldResource, oldStart }) => {
  const isStarted = oldStart <= nowUtc && oldEnd > nowUtc;

  if (!isStarted) {
    return null;
  }

  if (newStart !== oldStart) {
    return "Can't change start date.";
  }

  if (newResource !== oldResource) {
    return "Can't change instrument for a started booking.";
  }

  return null;
};

const hasTimelineDraftConflict = ({ bookingId, eventId, timelineEditDraft }) => {
  return Boolean(
    timelineEditDraft
    && (timelineEditDraft.entryId !== eventId || timelineEditDraft.bookingId !== bookingId),
  );
};

const getTimelineResourcePermissionMessage = ({ newResource, oldResource, permissions }) => {
  if (newResource === oldResource || permissions?.canBook !== false) {
    return null;
  }

  return permissions.reason || "You don't have access to this instrument";
};

const getResourceId = (equipment) => equipment?.inventoryId || equipment?.id;

export const normalizeAccessControlDataShape = (resource = {}) => ({
  ...resource,
  is_booking_controlled:
    resource.is_booking_controlled
    ?? (resource.isBookingControlled === undefined
      ? resource.is_booking_controlled
      : String(resource.isBookingControlled)),
  bookable_for_users: resource.bookable_for_users ?? resource.bookableForUsers,
  cidm_group_name: resource.cidm_group_name ?? resource.cidmGroupName,
  cidm_group_cidm_url:
    resource.cidm_group_cidm_url ?? resource.cidmGroupCidmUrl,
  cidm_group_more_info_url:
    resource.cidm_group_more_info_url ?? resource.cidmGroupMoreInfoUrl,
});

const sortByName = (firstItem, secondItem) => {
  return (firstItem?.name || "").localeCompare(secondItem?.name || "");
};

const normalizeEquipmentMeta = (equipment, resourceId) => {
  return {
    inventoryId: resourceId,
    shortIdentifier: equipment.shortIdentifier || equipment.name || equipment.equipmentModel || resourceId,
    equipmentModel: equipment.equipmentModel || equipment.modelName,
    equipmentId: equipment.equipmentId || "",
    location: equipment.location || equipment.siteName,
    siteName: equipment.siteName,
    cluster: equipment.cluster,
    equipmentCategory: equipment.equipmentCategory,
    ...equipment,
  };
};

const getGroupKey = (equipment, groupById) => {
  switch ((groupById || "EQUIPMENT").toUpperCase()) {
    case "CLUSTER":
      return equipment.cluster || "Empty";
    case "MODEL":
      return equipment.equipmentModel || "Empty";
    case "CATEGORY":
      return equipment.equipmentCategory || "Empty";
    case "LOCATION": {
      const loc = equipment.location;
      if (loc && typeof loc === "object") {
        const parts = [loc.building, loc.floor, loc.room].filter(Boolean);
        return parts.length > 0 ? parts.join(";") : "-";
      }
      return loc ?? "-";
    }
    case "EQUIPMENT":
    default:
      return null;
  }
};

const hasMatchingPendingReservations = ({ pendingReservations, selectedList }) => {
  const pendingResourceIds = new Set(
    (pendingReservations || []).map((reservation) => reservation?.resource).filter(Boolean),
  );

  return selectedList.every((resourceId) => pendingResourceIds.has(resourceId));
};

export const isExistingTimelineBookingEdit = ({ args, isEditMode, isTimelineView }) => {
  return Boolean(
    isTimelineView
      && !isEditMode
      && args.event?.bookingId
      && !args.event.id?.startsWith("available_"),
  );
};

export const buildUpdatedPendingReservations = (pendingReservations, event) => {
  return pendingReservations.map((item) => {
    if (item.id !== event.id) {
      return item;
    }

    return {
      ...item,
      start: event.start,
      end: event.end,
      resource: event.resource,
    };
  });
};

export const getExistingBookingEditErrorMessage = ({
  bookingId,
  canEditExistingBooking,
  eventId,
  newResource,
  newStart,
  nowUtc,
  oldEnd,
  oldResource,
  oldStart,
  pendingReservationsLength,
  permissions,
  timelineEditDraft,
}) => {
  const blockingMessage = [
    pendingReservationsLength > 0 && "Finish or cancel the new slot before editing an existing booking",
    !canEditExistingBooking && "You are not authorized to edit this slot",
    oldEnd <= nowUtc && "Past booking not allowed",
    getStartedBookingRestriction({ newResource, newStart, nowUtc, oldEnd, oldResource, oldStart }),
    hasTimelineDraftConflict({ bookingId, eventId, timelineEditDraft })
      && "You can only modify one slot at a time",
    getTimelineResourcePermissionMessage({ newResource, oldResource, permissions }),
  ].find(Boolean);

  return blockingMessage || null;
};

export const restoreOriginalDatepickerSelection = ({
  debouncedSetTempDates,
  originalSelectedEndDate,
  originalSelectedStartDate,
  setTemporaryEndDate,
  setTemporaryStartDate,
}) => {
  setTemporaryStartDate(originalSelectedStartDate);
  setTemporaryEndDate(originalSelectedEndDate);

  const restoredTempDates = originalSelectedStartDate && originalSelectedEndDate
    ? [originalSelectedStartDate, originalSelectedEndDate]
    : [];
  debouncedSetTempDates(restoredTempDates);
};

export const hasDatepickerRangeChanged = ({ selectedEndDate, selectedStartDate, value }) => {
  if (!value) {
    return false;
  }

  return (
    moment(value[0]).utc().format() !== moment(selectedStartDate).utc().format()
    || moment(value[1]).utc().format() !== moment(selectedEndDate).utc().format()
  );
};

export const isDatepickerEditRangeChangeInvalid = ({
  isOngoing,
  nextValue,
  selectedEndDate,
  selectedStartDate,
  siteTimezone,
}) => {
  const isDateChange = !(
    moment(selectedStartDate).utc().format() === moment(nextValue[0]).utc().format()
    && moment(selectedEndDate).utc().format() === moment(nextValue[1]).utc().format()
  );

  if (!isDateChange || nextValue[0] === nextValue[1]) {
    return true;
  }

  if (
    isOngoing
    && moment(nextValue[1]).tz(siteTimezone).utc().format()
      < moment.tz(siteTimezone).startOf("hour").utc().format()
  ) {
    return true;
  }

  return false;
};

export const syncSelectedDatesFromDatepicker = ({
  setSelectedEndDate,
  setSelectedStartDate,
  value,
}) => {
  const nextStartDate = value?.[1] ? value?.[0] : value?.[1];
  setSelectedStartDate(nextStartDate);
  setSelectedEndDate(value?.[1]);
};

export const getCalendarCssClass = ({
  hasBookableEquipment,
  isTimelineView,
  isTvMode,
  view,
}) => {
  const classNames = ["md-timeline-template"];

  if (isTimelineView) {
    classNames.push("equipment-view");
  }

  if (isTimelineView && !hasBookableEquipment) {
    classNames.push("no-bookable-equipment");
  }

  if (view === "week") {
    classNames.push("md-timeline-week-view");
  }

  classNames.push("md-timeline-consistent");

  if (isTvMode) {
    classNames.push("tv-mode");
  }

  return classNames.join(" ");
};

export const getClickToCreateValue = ({ isDatepickerOpen, isRecurringBooking, isTvMode }) => {
  return isTvMode || isRecurringBooking || isDatepickerOpen
    ? CLICK_TO_CREATE_DISABLED
    : CLICK_TO_CREATE_SINGLE;
};

export const resolveClickToCreateProp = (clickToCreateMode) => {
  return clickToCreateMode === CLICK_TO_CREATE_DISABLED ? false : clickToCreateMode;
};

export const canCreateBooking = ({
  hasBookableEquipment,
  hasServerError,
  pendingReservationsLength,
}) => {
  return pendingReservationsLength > 0 && hasBookableEquipment && !hasServerError;
};

export const getCreateBookingTitle = ({
  hasBookableEquipment,
  hasServerError,
  pendingReservationsLength,
}) => {
  if (hasServerError) {
    return "Unexpected Server Error, please try again later.";
  }

  if (pendingReservationsLength === 0) {
    return "Create time slots first";
  }

  if (!hasBookableEquipment) {
    return "No equipment available for booking";
  }

  return "Create booking for selected time slots";
};

export const getUnauthorizedEquipmentDetails = ({
  getEquipmentBookingStatus,
  pendingReservations,
  resourceData,
}) => {
  return pendingReservations
    .filter((reservation) => !getEquipmentBookingStatus(reservation.resource).canBook)
    .map((reservation) => {
      const equipment = resourceData.find((resource) => resource.id === reservation.resource);
      const status = getEquipmentBookingStatus(reservation.resource);

      return {
        name: equipment?.name || reservation.resource,
        reason: status.reason,
      };
    });
};

export const getUnauthorizedEquipmentMessage = (equipmentDetails) => {
  const equipmentNames = equipmentDetails.map((equipment) => equipment.name).join(", ");
  const reasons = [...new Set(equipmentDetails.map((equipment) => equipment.reason))].join("; ");

  return `Cannot create booking for: ${equipmentNames}. ${reasons}`;
};

export const buildEquipmentMetaById = ({ eventData, isTimelineView, resourceData }) => {
  const equipmentMetaById = new Map();

  (eventData || []).forEach((event) => {
    const equipment = event.equipment;
    const resourceId = equipment?.inventoryId || equipment?.addInfo?.inventoryId;
    if (resourceId && !equipmentMetaById.has(resourceId)) {
      equipmentMetaById.set(resourceId, equipment);
    }
  });

  (resourceData || []).forEach((equipment) => {
    const resourceId = getResourceId(equipment);
    if (!resourceId || (isTimelineView && equipmentMetaById.has(resourceId))) {
      return;
    }

    equipmentMetaById.set(resourceId, normalizeEquipmentMeta(equipment, resourceId));
  });

  return equipmentMetaById;
};

export const buildLeafResources = (equipmentMetaById) => {
  return Array.from(equipmentMetaById.values()).map((equipment) => ({
    id: equipment.inventoryId,
    name:
      equipment.shortIdentifier
      || `${equipment.equipmentModel}${equipment.equipmentId ? ` - ${equipment.equipmentId}` : ""}`,
    color: "#4981d6",
    eventCreation: true,
  }));
};

export const buildGroupedResources = ({ equipmentMetaById, groupBy, leafResources }) => {
  const groupByValue = (groupBy || "EQUIPMENT").toUpperCase();

  if (groupByValue === "EQUIPMENT") {
    return [...leafResources].sort((firstItem, secondItem) => firstItem.name.localeCompare(secondItem.name));
  }

  const buckets = new Map();

  leafResources.forEach((leafResource) => {
    const equipmentMeta = equipmentMetaById.get(leafResource.id);
    const key = (equipmentMeta && getGroupKey(equipmentMeta, groupByValue)) || "Empty";
    if (!buckets.has(key)) {
      buckets.set(key, []);
    }
    buckets.get(key).push(leafResource);
  });

  let groupIndex = 0;
  return Array.from(buckets.entries())
    .sort(([firstName], [secondName]) => firstName.localeCompare(secondName))
    .map(([groupName, items]) => ({
      id: `grp_${groupByValue.toLowerCase()}_${groupIndex++}`,
      name: groupName,
      eventCreation: false,
      collapsed: false,
      children: [...items].sort((firstItem, secondItem) => firstItem.name.localeCompare(secondItem.name)),
    }));
};

export const sortResourcesByAccessControl = ({
  equipmentMetaById,
  getCachedAccessConfigEnrichment,
  isTimelineView,
  resources,
  user,
}) => {
  if (!isTimelineView || !user || !Array.isArray(resources) || resources.length === 0) {
    return resources;
  }

  const trainingRequiredById = new Map();

  const isTrainingRequired = (resourceId) => {
    if (!resourceId) {
      return false;
    }

    if (trainingRequiredById.has(resourceId)) {
      return trainingRequiredById.get(resourceId);
    }

    const accessControlData = normalizeAccessControlDataShape({
      ...(equipmentMetaById?.get(resourceId) || {}),
      ...(getCachedAccessConfigEnrichment?.(resourceId) || {}),
      inventoryId: resourceId,
    });

    const { status, cidmGroupName } = getBookingAccessControlState(accessControlData, user);
    const trainingRequired =
      status === accessControlStatus.trainingRequired && Boolean(cidmGroupName);

    trainingRequiredById.set(resourceId, trainingRequired);
    return trainingRequired;
  };

  const sortLeafResources = (leafResources = []) => {
    return [...leafResources].sort((firstItem, secondItem) => {
      const firstTrainingRequired = isTrainingRequired(firstItem?.id);
      const secondTrainingRequired = isTrainingRequired(secondItem?.id);

      if (firstTrainingRequired !== secondTrainingRequired) {
        return firstTrainingRequired ? 1 : -1;
      }

      return sortByName(firstItem, secondItem);
    });
  };

  const hasGroupedResources = resources.some((resource) => Array.isArray(resource?.children));

  if (!hasGroupedResources) {
    return sortLeafResources(resources);
  }

  return resources
    .map((resource) => {
      if (!Array.isArray(resource?.children)) {
        return resource;
      }

      const sortedChildren = sortLeafResources(resource.children);
      const hasAnyBookableChild = sortedChildren.some((child) => !isTrainingRequired(child?.id));

      return {
        ...resource,
        children: sortedChildren,
        hasAnyBookableChild,
      };
    })
    .sort((firstItem, secondItem) => {
      const firstGroupBookable = firstItem?.hasAnyBookableChild;
      const secondGroupBookable = secondItem?.hasAnyBookableChild;

      if (firstGroupBookable !== secondGroupBookable) {
        return firstGroupBookable ? -1 : 1;
      }

      return sortByName(firstItem, secondItem);
    })
    .map(({ hasAnyBookableChild, ...resource }) => resource);
};

export const shouldSkipTimelineDateChangeOnCancel = ({
  fromCancelAction,
  pendingReservations,
  reason,
  selectedList,
}) => {
  if (!fromCancelAction || reason !== "cancel" || !selectedList?.length) {
    return false;
  }

  return hasMatchingPendingReservations({ pendingReservations, selectedList });
};

export const getOngoingEventListForDateChange = ({ isEditMode, isOngoing, pendingReservations }) => {
  if (!isEditMode || !isOngoing) {
    return [];
  }

  return pendingReservations.filter((reservation) => reservation.isOngoingEventFlag === true);
};

export const isSingleDayInDisplayTimezone = ({ displayTimeZone, endDate, startDate }) => {
  const displayDateOptions = { timeZone: displayTimeZone };
  const formattedStartDate = new Date(startDate).toLocaleString("en-US", displayDateOptions).split(",")[0];
  const formattedEndDate = new Date(endDate).toLocaleString("en-US", displayDateOptions).split(",")[0];

  return formattedStartDate === formattedEndDate;
};

export const getTimelineUpdateButtonTitle = ({ hasServerError }) => {
  if (hasServerError) {
    return "Unexpected Server Error, please try again later.";
  }

  return "Confirm timeline modifications";
};
